import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, MessageSquareText, XCircle } from 'lucide-react';

/**
 * Designed replacements for the browser's confirm(), prompt() and alert() boxes.
 * askConfirm / askPrompt return promises; notify shows a message. window.alert is routed to notify once the host is mounted.
 */
type Ask =
  | { kind: 'confirm'; message: string; title?: string; confirmLabel?: string; destructive?: boolean; resolve: (v: boolean) => void }
  | { kind: 'prompt'; message: string; title?: string; confirmLabel?: string; placeholder?: string; initial?: string; required?: boolean; multiline?: boolean; secret?: boolean; destructive?: boolean; resolve: (v: string | null) => void }
  | { kind: 'notice'; message: string; title?: string; tone: Tone; resolve: (v: true) => void };
type Tone = 'info' | 'success' | 'error' | 'warning';

let push: ((a: Ask) => void) | null = null;
const queue: Ask[] = [];
const send = (a: Ask) => (push ? push(a) : queue.push(a));

export function askConfirm(message: string, opts: { title?: string; confirmLabel?: string; destructive?: boolean } = {}): Promise<boolean> {
  return new Promise((resolve) => send({ kind: 'confirm', message, ...opts, resolve }));
}

/** Ask for a line (or paragraph) of text. Resolves to the text, or null when cancelled. */
export function askPrompt(message: string, opts: { title?: string; confirmLabel?: string; placeholder?: string; initial?: string; required?: boolean; multiline?: boolean; secret?: boolean; destructive?: boolean } = {}): Promise<string | null> {
  return new Promise((resolve) => send({ kind: 'prompt', message, required: true, ...opts, resolve }));
}

/** Show a message in the app (replaces alert()). Tone is guessed from the words when not given. */
export function notify(message: string, opts: { title?: string; tone?: Tone } = {}): Promise<true> {
  const tone: Tone = opts.tone ?? (/fail|error|could not|couldn|not allowed|invalid|wrong|denied|unable|cannot/i.test(message) ? 'error'
    : /saved|success|done|sent|created|updated|deleted|copied|published|recorded|approved/i.test(message) ? 'success' : 'info');
  return new Promise((resolve) => send({ kind: 'notice', message, title: opts.title, tone, resolve }));
}

const TONE: Record<Tone, { icon: typeof Info; box: string; title: string }> = {
  info: { icon: Info, box: 'bg-[#0A2540]/5 text-[#0A2540]', title: 'Please note' },
  success: { icon: CheckCircle2, box: 'bg-green-50 text-green-600', title: 'Done' },
  error: { icon: XCircle, box: 'bg-red-50 text-red-600', title: 'Something went wrong' },
  warning: { icon: AlertTriangle, box: 'bg-amber-50 text-amber-600', title: 'Heads up' },
};

/** Mount once near the app root. */
export function ConfirmHost() {
  const [items, setItems] = useState<Ask[]>([]);
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  useEffect(() => {
    push = (a) => setItems((s) => [...s, a]);
    if (queue.length) setItems((s) => [...s, ...queue.splice(0)]);
    const native = window.alert;
    window.alert = (m?: unknown) => { void notify(String(m ?? '')); };
    return () => { push = null; window.alert = native; };
  }, []);
  const ask = items[0];
  useEffect(() => { if (ask?.kind === 'prompt') { setText(ask.initial ?? ''); setTimeout(() => inputRef.current?.focus(), 30); } }, [ask]);
  if (!ask) return null;

  const close = () => setItems((s) => s.slice(1));
  const cancel = () => { if (ask.kind === 'confirm') ask.resolve(false); else if (ask.kind === 'prompt') ask.resolve(null); else ask.resolve(true); close(); };
  const ok = () => {
    if (ask.kind === 'confirm') ask.resolve(true);
    else if (ask.kind === 'prompt') { if (ask.required && !text.trim()) return; ask.resolve(text.trim()); }
    else ask.resolve(true);
    close();
  };

  if (ask.kind === 'notice') {
    const T = TONE[ask.tone];
    return (
      <div className="fixed inset-0 z-[200] bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center" onMouseDown={(e) => e.target === e.currentTarget && ok()} role="alertdialog" aria-modal="true">
        <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl shadow-2xl p-5">
          <div className="flex items-start gap-3">
            <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${T.box}`}><T.icon className="w-5 h-5" /></span>
            <div className="min-w-0"><h3 className="font-bold text-slate-900">{ask.title ?? T.title}</h3><p className="text-sm text-slate-600 mt-1 whitespace-pre-line break-words">{ask.message}</p></div>
          </div>
          <button autoFocus onClick={ok} className="w-full mt-5 py-3 rounded-xl bg-[#0A2540] text-white text-sm font-bold">OK</button>
        </div>
      </div>
    );
  }

  const danger = ask.destructive ?? /delete|remove|cancel|reject|decline|revoke|purge|void|write.?off/i.test(ask.message + (ask.title ?? ''));
  const isPrompt = ask.kind === 'prompt';
  return (
    <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onMouseDown={(e) => e.target === e.currentTarget && cancel()} role="dialog" aria-modal="true">
      <form onSubmit={(e) => { e.preventDefault(); ok(); }} className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl p-5" onKeyDown={(e) => e.key === 'Escape' && cancel()}>
        <div className="flex items-start gap-3">
          <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${danger ? 'bg-red-50 text-red-600' : 'bg-[#0A2540]/5 text-[#0A2540]'}`}>{isPrompt ? <MessageSquareText className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}</span>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-slate-900">{ask.title ?? (isPrompt ? 'A quick note' : 'Please confirm')}</h3>
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{ask.message}</p>
          </div>
        </div>
        {isPrompt && (ask.multiline
          ? <textarea ref={inputRef} rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={ask.placeholder} className="w-full mt-4 rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 outline-none" />
          : <input ref={inputRef} type={ask.secret ? 'password' : 'text'} autoComplete={ask.secret ? 'current-password' : undefined} value={text} onChange={(e) => setText(e.target.value)} placeholder={ask.placeholder} className="w-full mt-4 rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 outline-none" />)}
        <div className="flex gap-2 mt-5">
          <button type="button" autoFocus={!isPrompt} onClick={cancel} className="flex-1 py-3 rounded-xl border border-slate-300 text-sm font-bold text-slate-700">Cancel</button>
          <button type="submit" disabled={isPrompt && ask.required && !text.trim()} className={`flex-1 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50 ${danger ? 'bg-red-600' : 'bg-[#0A2540]'}`}>{ask.confirmLabel ?? (isPrompt ? 'Continue' : 'Yes, go ahead')}</button>
        </div>
      </form>
    </div>
  );
}
